/* Pure launch pricing rules. Configuration and backend activation belong to callers. */
((scope) => {
  const money = value => Number.isSafeInteger(value) && value >= 0;
  const maximum = BigInt(Number.MAX_SAFE_INTEGER);
  const invalid = error => ({ error, subtotal: null, eligibleSubtotal: null, paidEligibleSubtotal: null, discountPercent: null, discountAmount: null, total: null, lines: [], remaining: null });

  // Represent decimal percentages exactly, including scientific notation.
  function percentage(value) {
    const [decimal, exponent = '0'] = String(value).split('e');
    const places = (decimal.split('.')[1] || '').length - Number(exponent);
    const numerator = BigInt(decimal.replace('.', ''));
    return places >= 0
      ? { numerator, denominator: 100n * (10n ** BigInt(places)) }
      : { numerator: numerator * (10n ** BigInt(-places)), denominator: 100n };
  }

  function quote(rows, config, { currency = 'SEK', hasCodes = false } = {}) {
    if (hasCodes) return invalid('codes');
    if (!config || typeof config.currency !== 'string' || !config.currency || config.currency !== currency) return invalid('currency');
    if (!money(config.threshold) || !Number.isFinite(config.percent) || config.percent < 0 || config.percent > 100) return invalid('config');
    if (!Array.isArray(rows)) return invalid('rows');

    let subtotal = 0n, eligibleSubtotal = 0n, paidEligibleSubtotal = 0n;
    const lines = [];
    for (const row of rows) {
      if (!row || !money(row.unitPrice) || !money(row.quantity)) return invalid('rows');
      const amount = BigInt(row.unitPrice) * BigInt(row.quantity);
      subtotal += amount;
      if (subtotal > maximum) return invalid('overflow');
      if (row.eligible === true) {
        const fullPrice = config.discountBase === 'price_before_selling_plan';
        if (fullPrice && !money(row.fullPrice)) return invalid('rows');
        eligibleSubtotal += fullPrice ? BigInt(row.fullPrice) * BigInt(row.quantity) : amount;
        paidEligibleSubtotal += amount;
        if (eligibleSubtotal > maximum) return invalid('overflow');
      }
      lines.push({ id: row.id, quantity: row.quantity, unitPrice: row.unitPrice, discountAmount: 0, total: Number(amount) });
    }

    const qualifies = eligibleSubtotal > 0n && eligibleSubtotal >= BigInt(config.threshold);
    const discountPercent = qualifies ? config.percent : 0;
    const ratio = percentage(discountPercent);
    const unrounded = paidEligibleSubtotal * ratio.numerator;
    const discount = (unrounded * 2n + ratio.denominator) / (ratio.denominator * 2n);
    if (discount > 0n) {
      let allocated = 0n;
      const shares = [];
      lines.forEach((line, index) => {
        if (rows[index].eligible !== true || !line.total) return;
        const share = discount * BigInt(line.total);
        const amount = share / paidEligibleSubtotal;
        allocated += amount;
        shares.push({ index, amount, remainder: share % paidEligibleSubtotal });
      });
      // Largest remainders receive the remaining cents; input order resolves ties.
      shares.sort((a, b) => a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1);
      let remainder = discount - allocated;
      for (const share of shares) {
        const amount = Number(share.amount + (remainder > 0n ? 1n : 0n));
        if (remainder > 0n) remainder -= 1n;
        lines[share.index].discountAmount = amount;
        lines[share.index].total -= amount;
      }
    }
    return {
      error: null, subtotal: Number(subtotal), eligibleSubtotal: Number(eligibleSubtotal), paidEligibleSubtotal: Number(paidEligibleSubtotal),
      discountPercent, discountAmount: Number(discount), total: Number(subtotal - discount), lines,
      remaining: Math.max(0, config.threshold - Number(eligibleSubtotal))
    };
  }

  function shipping(total, config, { country = 'SE', currency = 'SEK', requiresShipping = true } = {}) {
    if (requiresShipping === false) return { dhl: 0, flex: 0, home: 0 };
    if (country !== 'SE' || currency !== 'SEK' || config?.currency !== currency || !money(total)) return null;
    const rates = config.shipping;
    if (!rates || !['threshold', 'dhl', 'flex', 'home', 'flexAbove', 'homeAbove'].every(key => money(rates[key]))) return null;
    return total >= rates.threshold
      ? { dhl: 0, flex: rates.flexAbove, home: rates.homeAbove }
      : { dhl: rates.dhl, flex: rates.flex, home: rates.home };
  }

  function allowedPlans(plans, config) {
    const weeks = config?.weeks;
    if (!Array.isArray(plans) || !Array.isArray(weeks) || !weeks.every(week => Number.isSafeInteger(week) && week > 0)) return [];
    return plans.filter(plan => plan && String(plan.interval).toUpperCase() === 'WEEK'
      && Number.isFinite(Number(plan.intervalCount)) && weeks.includes(Number(plan.intervalCount)));
  }

  function mode(config, { previewRequested = false } = {}) {
    if (previewRequested && config?.previewAllowed === true) return 'preview';
    return config?.enabled === true ? 'active' : 'legacy';
  }

  const api = { quote, shipping, allowedPlans, mode };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (scope) scope.MettaV2Rules = api;
})(typeof window !== 'undefined' ? window : null);
